import { FC, useCallback, useEffect, useState } from 'react'
import { useCookie } from 'react-use'

import { Flex } from 'antd'

import { useLoading } from '@data-client/hooks'
import { useController } from '@data-client/react'

import { Button } from '@peoplevine/sdk/components/button/Button'
import { CheckableGroup } from '@peoplevine/sdk/components/checkable'
import { HorizontalSpace, VerticalSpace } from '@peoplevine/sdk/components/layout'
import { useResponsive } from '@peoplevine/sdk/components/screen/Breakpoint'
import Text from '@peoplevine/sdk/components/text/Text'
import { Title } from '@peoplevine/sdk/components/text/Title'
import { AccountIsRegistered } from '@peoplevine/sdk/datasource/account/account'
import { type VerifyMethod, VerifyResource } from '@peoplevine/sdk/datasource/verify'
import { useFeatureToggles } from '@peoplevine/sdk/providers/Feature'
import { useNotification } from '@peoplevine/sdk/providers/Notification'

import VerifySMSCode from './verify/VerifySmsCode'

const SendAccountActivation: FC<AccountIsRegistered> = ({ customerId, firstName, mobileNumber, email }) => {
  const { fetch } = useController()
  const { isMobile } = useResponsive()
  const { notifyOnError } = useNotification()
  const { hasSms } = useFeatureToggles()
  const [activationSent, setActivationSent] = useCookie('activation-link')
  const [method, setMethod] = useState<VerifyMethod>(!hasSms ? 'email' : 'sms')

  useEffect(() => {
    !mobileNumber && setMethod('email')
  }, [mobileNumber])

  const handleEmailActivate = useCallback(
    async (params: { customerId: Data.ID; method: VerifyMethod }) => {
      await fetch(VerifyResource.activation.send, {
        ...params,
        callback: `${import.meta.env.PUBLIC_URL ?? ''}/activate`,
      })
        .then((response) => response.success && setActivationSent(method))
        .catch(notifyOnError)
    },
    [fetch, method, notifyOnError, setActivationSent],
  )

  const [sendActivationLink, loading] = useLoading(handleEmailActivate, [])

  return activationSent ? (
    activationSent === 'sms' ? (
      customerId && <VerifySMSCode customerId={customerId} />
    ) : (
      <VerticalSpace size={16}>
        <Title level={1} style={{ marginBottom: 0 }}>
          Activation Link Sent
        </Title>
        <Flex vertical gap={2}>
          <Text context={{ email }}>{"We've sent an activation link to {email}"}</Text>
          <Text>Click the link in the email to setup a new password</Text>
        </Flex>
      </VerticalSpace>
    )
  ) : (
    <VerticalSpace size={'large'}>
      <VerticalSpace>
        <Title level={1} style={{ marginBottom: 0 }}>
          {'Activate Account'}
        </Title>
        <Text context={{ firstName }}>{'Welcome to your membership account, {firstName}!'}</Text>
        <Text type={'secondary'}>
          {hasSms
            ? `Select a method below to begin the activation process`
            : `Before accessing your account we need to verify your email address`}
        </Text>
      </VerticalSpace>

      <VerticalSpace size={'large'}>
        <CheckableGroup<VerifyMethod>
          defaultValue={method}
          onChange={(value) => value && setMethod(value)}
          items={[
            {
              title: (
                <Text strong>
                  <HorizontalSpace align={'center'}>
                    <Text>SMS Text Message</Text>
                    {mobileNumber && (
                      <Text type={'secondary'} translate={'no'}>
                        ({mobileNumber.replaceAll('*', '•')})
                      </Text>
                    )}
                  </HorizontalSpace>
                </Text>
              ),
              description: <Text>Receive a text message with an activation code</Text>,
              value: 'sms',
              icon: !isMobile ? 'application/mobile' : undefined,
              hidden: !mobileNumber || !hasSms,
            },
            {
              title: (
                <Text strong>
                  <HorizontalSpace align={'center'}>
                    <Text>Email</Text>
                    {email && (
                      <Text size={'small'} translate={'no'} type={'secondary'}>
                        ({email})
                      </Text>
                    )}
                  </HorizontalSpace>
                </Text>
              ),
              description: <Text>Receive an email with an activation link</Text>,
              value: 'email',
              icon: !isMobile ? 'application/mail' : undefined,
            },
          ]}
        />
        <Button loading={loading} onClick={() => sendActivationLink({ customerId, method })} block disabled={!method}>
          Send Activation Link
        </Button>
      </VerticalSpace>
    </VerticalSpace>
  )
}

export default SendAccountActivation
